import { Fragment, useMemo, useRef, useState } from 'react'
import { RESUMED_QUIETLY_MS, fingerprint, useToolProgress } from './progress'
import { useI18n } from '@/i18n/I18nProvider'
import { DIAGNOSTIC_SCORES } from '@/content/tools/diagnostic'
import LeadGate from './LeadGate'
import { band } from './stepBands'
import type { SubmitState, ToolResult } from './submit'

/**
 * The Transformation Readiness Diagnostic.
 *
 * Eight questions, two axes — Will and Skill — and a quadrant rather than a
 * single number. That last part is the whole design: "high will, low skill" and
 * "low will, high skill" can average to the same score and mean opposite
 * things, so this tool refuses to collapse them.
 *
 * ---------------------------------------------------------------------------
 * WHERE THE SCORES LIVE, which is the one thing to understand before editing.
 *
 * The options are in a deliberately SHUFFLED order — the best answer is not
 * last, because a reader who notices that the last option always flatters them
 * stops answering and starts positioning.
 *
 * The numbers are therefore NOT part of the question data. They live in
 * `DIAGNOSTIC_SCORES`, outside the dictionary, indexed by question and option
 * position; every string comes from `t.tools.diagnostic`. That split exists
 * because `Translation` treats arrays as atomic, so a per-option score inside
 * `questions` would have forced every locale to restate all thirty-two of them —
 * and a translator mistyping a number would silently change what the tool
 * measures, with the compiler satisfied.
 * ---------------------------------------------------------------------------
 */

/** Raw 4–16 per axis → 0–100. The source's formula, unchanged. */
const pct = (raw: number) => Math.round(((raw - 4) / 12) * 100)

type Quadrant = 'UNSTOP' | 'PIPE' | 'BLIND' | 'DRIFT'

function quadrant(will: number, skill: number): Quadrant {
  if (will >= 50 && skill >= 50) return 'UNSTOP'
  if (will >= 50) return 'PIPE'
  if (skill >= 50) return 'BLIND'
  return 'DRIFT'
}

export default function Diagnostic() {
  const { t } = useI18n()
  const d = t.tools.diagnostic
  const ui = t.tools.ui

  const openedAt = useRef(Date.now()).current

  const [phase, setPhase] = useState<'intro' | 'quiz' | 'gate' | 'report'>('intro')
  const [idx, setIdx] = useState(0)
  const [answers, setAnswers] = useState<(number | null)[]>(() =>
    DIAGNOSTIC_SCORES.map(() => null),
  )
  const [saved, setSaved] = useState<SubmitState>({ status: 'idle' })
  /** Set by pressing Next with nothing chosen — see the note beside the button. */
  const [needAnswer, setNeedAnswer] = useState(false)

  /*
    HALF-FINISHED ANSWERS SURVIVE A REFRESH — in this browser only. The whole
    argument for that, including why it is deliberately not a row in our
    database, is at the top of `tools/progress.ts`; the short version is that at
    this point the reader has given us no email and agreed to nothing.

    Only `answers` and the question index are kept. Not `phase`: restoring
    somebody straight into the gate would put a form in front of a reader who
    has no memory of finishing, and not `saved`, which describes a submission
    that by definition has not happened yet.
  */
  const progress = useToolProgress({
    tool: 'transformation-readiness',
    shape: fingerprint(DIAGNOSTIC_SCORES),
    state: useMemo(() => ({ idx, answers }), [idx, answers]),
    active: phase === 'quiz' || phase === 'gate',
    restore: (saved, ageMs) => {
      // Untrusted: this is JSON out of the reader's own storage. Every value is
      // checked against the CURRENT question set before it is applied, so a
      // hand-edited or stale entry cannot score somebody on answers they did
      // not give — it just fails the checks and is ignored.
      if (!saved || !Array.isArray(saved.answers)) return
      if (saved.answers.length !== DIAGNOSTIC_SCORES.length) return
      const clean = saved.answers.map((a, i) =>
        typeof a === 'number' && Number.isInteger(a) && a >= 0 && a < DIAGNOSTIC_SCORES[i].length
          ? a
          : null,
      )
      if (!clean.some((a) => a !== null)) return
      setAnswers(clean)
      setIdx(
        typeof saved.idx === 'number'
          ? Math.min(Math.max(Math.trunc(saved.idx), 0), DIAGNOSTIC_SCORES.length - 1)
          : 0,
      )
      // A refresh puts them back in the quiz with no announcement. A return
      // visit stays on the intro and is offered the choice instead.
      if (ageMs < RESUMED_QUIETLY_MS) setPhase('quiz')
    },
  })

  /**
   * Scores come from `DIAGNOSTIC_SCORES`, text from the reader's locale.
   *
   * The numbers live outside the dictionary so no translation can alter them —
   * see that export's comment for why that mattered enough to shape the file.
   */
  const { will, skill } = useMemo(() => {
    const raw = DIAGNOSTIC_SCORES.map((opts, i) =>
      answers[i] === null ? 1 : opts[answers[i] as number],
    )
    const sum = (a: number[]) => a.reduce((x, y) => x + y, 0)
    return { will: pct(sum(raw.slice(0, 4))), skill: pct(sum(raw.slice(4, 8))) }
  }, [answers])

  const key = quadrant(will, skill)
  const A = d.archetypes[key]

  const result: ToolResult = useMemo(
    () => ({
      // The headline number is the WEAKER axis, not an average.
      //
      // No single number can carry a two-axis result — that is this tool's whole
      // thesis — so the question is only which distortion to accept. An average
      // would report a Pipe Dream (will 100, skill 0) as a 50: mid-table in a
      // CRM list, indistinguishable from a genuinely balanced organisation, and
      // the constraint that predicts failure hidden. The minimum instead says
      // "something here is at zero", which is true and actionable.
      //
      // Its own cost, stated so nobody rediscovers it as a bug: a Pipe Dream and
      // a Drifter both report 0. `verdict` is what separates them in the CRM,
      // and `dimensions` carries both axes for anyone who looks.
      score: Math.min(will, skill),
      verdict: A.name,
      band: key.toLowerCase(),
      dimensions: { [d.ui.ax1name]: will, [d.ui.ax2name]: skill },
      answers: Object.fromEntries(
        DIAGNOSTIC_SCORES.map((_, i) => [
          d.questions[i].text,
          answers[i] === null ? null : d.questions[i].options[answers[i] as number],
        ]),
      ),
    }),
    [will, skill, A, key, d, answers],
  )

  // ---- intro -------------------------------------------------------------
  if (phase === 'intro') {
    // Wider than the other phases, and that is what pays for the larger type:
    // asked on 2026-08-26 for a shorter grey band with bigger words in it, and
    // those pull in opposite directions at a 3xl measure — 18px in a 372px
    // column simply wraps to more lines and the band grows taller. At 4xl each
    // card is ~430px, so the same sentences occupy about the same number of
    // lines one step up the scale, and the height comes off the padding
    // instead. The same round, the same reasoning and the same sizes as the
    // Agentic Readiness Index's intro — the two pages sit one click apart and
    // a reader should not meet two different type scales in the same band.
    return (
      <div className="mx-auto max-w-4xl">
        {/*
          The button leads and the explanation follows, at a size above the
          site's standard pill — asked for on 2026-08-26. It used to sit under
          the two axis cards, a screen below the headline, so the page opened
          with reading rather than with the one thing a visitor came to do.
          There is exactly one action on a tool page and it should look like it.
        */}
        <div className="text-center">
          <button className="btn-primary px-12 py-5 text-xl" onClick={() => setPhase('quiz')}>
            {ui.start}
          </button>
          <p className="mt-4 text-base text-muted-foreground">
            {d.ui.qcount.replace('{n}', String(d.questions.length))}
          </p>
          {/*
            Only reached when progress was found AND is old enough that the
            reader will not remember leaving — a refresh never gets here,
            because `restore` already put them back in the quiz.
          */}
          {progress.resumedAt !== null && (
            <div className="mt-6">
              <p className="text-base text-muted-foreground">{ui.resumeNote}</p>
              <button
                className="mt-2 underline"
                onClick={() => setPhase('quiz')}
              >
                {ui.resume}
              </button>
            </div>
          )}
        </div>
        {/* 2rem of air under the button rather than 3rem: the band lost its
            padding in the same round, and a gap that large inside it just put
            the height back. */}
        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          {[
            { tag: d.ui.ax1tag, card: d.ui.ax1card, q: d.ui.ax1q, items: d.ui.ax1items },
            { tag: d.ui.ax2tag, card: d.ui.ax2card, q: d.ui.ax2q, items: d.ui.ax2items },
          ].map((ax) => (
            <div key={ax.tag} className="rounded-2xl border border-border p-6">
              <p className="text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                {ax.tag}
              </p>
              <p className="mt-2 text-xl font-bold text-foreground">{ax.card}</p>
              <p className="mt-2 text-lg italic leading-relaxed text-muted-foreground">{ax.q}</p>
              <ul className="mt-4 space-y-2">
                {ax.items.map((it) => (
                  <li key={it} className="text-base leading-relaxed text-muted-foreground">
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // ---- questions ---------------------------------------------------------
  if (phase === 'quiz') {
    const q = d.questions[idx]
    const answered = answers[idx] !== null
    const last = idx === DIAGNOSTIC_SCORES.length - 1

    return (
      <div className="mx-auto max-w-2xl sm:max-w-4xl">
        <div className="flex items-baseline justify-between text-sm text-muted-foreground">
          <span>
            {ui.question} {idx + 1} / {DIAGNOSTIC_SCORES.length}
          </span>
          <span className="font-semibold text-foreground">{q.dim}</span>
        </div>
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-border">
          <div
            className="h-full bg-foreground transition-[width] [transition-duration:var(--transition-base)]"
            style={{ width: `${(idx / DIAGNOSTIC_SCORES.length) * 100}%` }}
          />
        </div>

        {/*
          Every question is laid out in the SAME grid, and the grid has one ROW
          PER PART: the question, then the options. Each question puts its own
          copy of a part in that part's row, so the row is as tall as the longest
          instance across the set and every part starts at the same y on every
          question — the question text and the first option each keep one fixed
          position, and the Back/Next row never moves.

          This replaces a `sm:min-h-[34rem]` guess, and then a single shared cell
          per question. The reserved minimum only held while every question fitted
          inside it. The shared cell held the block still but not its contents: a
          two-line question pushed the options down and a one-line question pulled
          them back up, which is the movement the screenshots were about.
          Measuring the set band by band needs no number and works at every width.
          `invisible` keeps the row open; `inert` keeps the hidden question's
          buttons out of the tab order and away from screen readers.
        */}
        {/*
          One grid holds the question column and both buttons: from `sm` up the
          buttons are the left and right rails beside the options, vertically
          centred; on a phone there is no room for rails, so they drop to a row
          underneath. Placing them by grid area rather than duplicating the JSX
          keeps one Back and one Next, with one set of handlers.
        */}
        <div className="mt-8 grid grid-cols-2 items-center gap-4 sm:grid-cols-[auto_1fr_auto] sm:gap-6">
          <div className="col-span-2 row-start-1 grid min-w-0 gap-y-6 sm:col-span-1 sm:col-start-2">
          {d.questions.map((qq, qi) => {
            const on = qi === idx
            return (
            <Fragment key={qi}>
              <h3
                className={
                  band(on) +
                  ' row-start-1 text-2xl font-bold leading-tight tracking-tight text-foreground'
                }
                inert={on ? undefined : true}
              >
                {qq.text}
              </h3>

              <div
                className={band(on) + ' row-start-2 space-y-3'}
                inert={on ? undefined : true}
                // Masked in session replay. D56 refused to store these answers
                // on our servers before the reader has given us an email or
                // agreed to anything — a recording that shows which option they
                // clicked would collect the same thing through the side door.
                data-private
              >
                {qq.options.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      const next = [...answers]
                      next[qi] = i
                      setAnswers(next)
                    }}
                    className={
                      'flex w-full items-start gap-4 rounded-xl border p-5 text-left text-base leading-relaxed transition-colors [transition-duration:var(--transition-base)] ' +
                      (answers[qi] === i
                        ? 'border-foreground bg-muted font-medium text-foreground'
                        : 'border-border bg-background text-muted-foreground hover:border-foreground/40')
                    }
                  >
                    <span
                      className={
                        'mt-1 size-4 flex-none rounded-full border-2 ' +
                        (answers[qi] === i ? 'border-foreground bg-foreground' : 'border-border')
                      }
                    />
                    <span>{opt}</span>
                  </button>
                ))}
              </div>
            </Fragment>
            )
          })}
          </div>

          <button
            className="btn-secondary row-start-2 justify-self-start whitespace-nowrap sm:col-start-1 sm:row-start-1"
            onClick={() => {
              setNeedAnswer(false)
              setIdx((n) => Math.max(0, n - 1))
            }}
            style={{ visibility: idx === 0 ? 'hidden' : 'visible' }}
          >
            {d.ui.back}
          </button>
          {/*
            Both labels are always in the button, one invisible on top of the
            other, so it is as wide as the longer of the two on every question:
            swapping "Next" for "See the verdict" on the last step otherwise
            widened the right-hand rail and shifted the question column sideways.
          */}
          <button
            className="btn-primary row-start-2 grid justify-items-center justify-self-end whitespace-nowrap sm:col-start-3 sm:row-start-1"
            onClick={() => {
              if (!answered) return setNeedAnswer(true)
              setNeedAnswer(false)
              return last ? setPhase('gate') : setIdx((n) => n + 1)
            }}
          >
            <span className={'col-start-1 row-start-1' + (last ? '' : ' invisible')}>
              {ui.seeVerdict}
            </span>
            <span className={'col-start-1 row-start-1' + (last ? ' invisible' : '')}>
              {ui.next}
            </span>
          </button>
        </div>

        {/*
          Pressing Next with nothing chosen used to do nothing at all: the button
          was `disabled`, which reads as broken rather than as "answer first". It
          says so now, and the note clears itself the moment an option is picked.
          It sits BELOW the buttons rather than above them: appearing above moved
          the whole row down by its own height, which is the other half of the
          jump this screen was reported for.
        */}
        {needAnswer && !answered && (
          <p role="alert" className="mt-4 text-sm font-medium text-destructive">
            {last ? ui.needAnswerLast : ui.needAnswer}
          </p>
        )}
      </div>
    )
  }

  if (phase === 'gate') {
    return (
      <LeadGate
        tool="transformation-readiness"
        result={result}
        openedAt={openedAt}
        onDone={(state) => {
          setSaved(state)
          setPhase('report')
          // The run is over. Leaving it stored would offer a finished session
          // back as unfinished work on the reader's next visit.
          progress.forget()
        }}
      />
    )
  }

  // ---- the report --------------------------------------------------------
  const cases = A.cases.map((c) => d.cases[c as keyof typeof d.cases]).filter(Boolean)

  return (
    <div className="mx-auto max-w-3xl">
      {saved.status === 'failed' && (
        <p className="mb-6 rounded-lg border border-border bg-muted p-4 text-sm text-muted-foreground">
          {ui.notSaved}
        </p>
      )}

      <p className="text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {d.ui.yourprofile}
      </p>
      <h3 className="mt-3 text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">
        {A.name}
      </h3>
      <p className="mt-2 text-xl italic text-muted-foreground">{A.tag}</p>

      {/* Both axes, side by side. Never one number — see the component header. */}
      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        {[
          { name: d.ui.ax1name, value: will },
          { name: d.ui.ax2name, value: skill },
        ].map((ax) => (
          <div key={ax.name} className="rounded-2xl border border-border p-6">
            <p className="text-sm font-semibold text-muted-foreground">{ax.name}</p>
            <p className="mt-1 text-4xl font-bold tabular-nums text-foreground">{ax.value}</p>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-border">
              <div
                className="h-full rounded-full bg-foreground"
                style={{ width: `${ax.value}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 space-y-8">
        {[
          { label: d.ui.picture, body: A.picture },
          { label: d.ui.internal, body: A.internal },
          { label: d.ui.external, body: A.external },
          { label: d.ui.odds, body: A.odds },
        ].map((block) => (
          <div key={block.label}>
            <p className="text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              {block.label}
            </p>
            <p className="mt-2 text-lg leading-relaxed text-muted-foreground">{block.body}</p>
          </div>
        ))}
      </div>

      <div className="mt-12 rounded-2xl border-l-4 border-l-foreground bg-muted p-6">
        <p className="text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {d.ui.imperative}
        </p>
        <p className="mt-2 text-xl font-bold text-foreground">{A.impT}</p>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">{A.impP}</p>
      </div>

      {/* Per-dimension breakdown, using each question's short label. */}
      <div className="mt-12">
        <p className="text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {d.ui.breakdown}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">{d.ui.breakdownsub}</p>
        <div className="mt-5 space-y-4">
          {d.questions.map((q, i) => {
            const s = answers[i] === null ? 1 : DIAGNOSTIC_SCORES[i][answers[i] as number]
            return (
              <div key={i}>
                <div className="flex items-baseline justify-between">
                  <p className="text-base font-semibold text-foreground">{q.short}</p>
                  <p className="text-sm tabular-nums text-muted-foreground">{s} / 4</p>
                </div>
                <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-border">
                  <div
                    className="h-full rounded-full bg-foreground"
                    style={{ width: `${(s / 4) * 100}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* The named precedents. Two failures, two successes — see the check: note. */}
      {cases.length > 0 && (
        <div className="mt-12">
          <p className="text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            {d.ui.precedent}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{d.ui.precedentsub}</p>
          <div className="mt-5 space-y-6">
            {cases.map((c) => (
              <div
                key={c.name}
                className={
                  'rounded-2xl border p-6 ' +
                  (c.kind === 'fail' ? 'border-border bg-muted' : 'border-foreground/30')
                }
              >
                <p className="text-base font-bold text-foreground">{c.name}</p>
                <p className="mt-2 text-base leading-relaxed text-muted-foreground">{c.p}</p>
                {c.l && (
                  <p className="mt-3 text-sm font-medium italic text-foreground">{c.l}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-14 border-t border-border pt-10 text-center">
        <p className="text-xl font-semibold leading-snug text-foreground">{ui.ctaHeading}</p>
        {/* `#contact` rather than the Contact Us page: the form is at the foot
            of this very page, so the button scrolls to it. */}
        <a className="btn-primary mt-6 inline-flex" href="#contact">
          {ui.ctaLabel}
        </a>
        <p className="mt-6 text-sm text-muted-foreground">
          <button
            className="underline"
            onClick={() => {
              setAnswers(DIAGNOSTIC_SCORES.map(() => null))
              setIdx(0)
              setSaved({ status: 'idle' })
              setPhase('intro')
              progress.forget()
            }}
          >
            {d.ui.retake}
          </button>
        </p>
      </div>
    </div>
  )
}
